import { useCallback, useEffect, useState } from "react";
import { Download, Search, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { Pick } from "@/components/event-setup/BasicsSection";
import { api, fmtDate } from "@/lib/api";
import { useCanExport } from "@/context/AppContext";
import { Pager, usePaged } from "@/components/Pager";

const CATEGORIES = ["Sign-in", "Staff", "Members", "Events", "Sponsors", "Master list", "Payments", "Registrations", "Special receipts", "Gate", "Site settings", "Settings"];
const ALL = "__all";
const EMPTY = { category: ALL, actor_id: ALL, q: "", date_from: "", date_to: "" };

const exportCsv = (rows) => {
  const head = ["When", "Staff", "Username", "Role", "Category", "Action", "Affected", "Details"];
  const body = rows.map((r) => [r.created_at, r.actor_name, r.actor_username, r.actor_role, r.category, r.action, r.target, r.details]);
  const csv = [head, ...body].map((x) => x.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "gamls-activity-log.csv";
  a.click();
};

const Filters = ({ f, set, staff }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_180px_200px_150px_150px_auto] gap-2 p-4 border-b">
    <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search action, item, details or staff" className="pl-9 h-10" data-testid="activity-search-input" /></div>
    <Pick value={f.category} onChange={(v) => set({ category: v })} testId="activity-category-filter" options={[{ value: ALL, label: "All categories" }, ...CATEGORIES]} />
    <Pick value={f.actor_id} onChange={(v) => set({ actor_id: v })} testId="activity-staff-filter" options={[{ value: ALL, label: "All staff" }, ...staff.map((s) => ({ value: s.id, label: `${s.name} (@${s.username})` }))]} />
    <Input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} className="h-10" aria-label="From date" data-testid="activity-date-from" />
    <Input type="date" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} className="h-10" aria-label="To date" data-testid="activity-date-to" />
    <BrandButton variant="outline" onClick={() => set(EMPTY)} data-testid="activity-reset-filters"><RotateCcw className="w-4 h-4" />Clear</BrandButton>
  </div>
);

const Row = ({ r }) => (
  <tr className="align-top" data-testid="activity-row">
    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
    <td className="px-4 py-3 whitespace-nowrap"><div className="font-medium">{r.actor_name}</div><div className="text-xs text-gray-500">@{r.actor_username} · {r.actor_role}</div></td>
    <td className="px-4 py-3 whitespace-nowrap"><span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">{r.category}</span></td>
    <td className={`px-4 py-3 whitespace-nowrap font-semibold ${/Failed|Voided|Deleted|Deactivated|Reset/.test(r.action) ? "text-[#E60000]" : ""}`}>{r.action}</td>
    <td className="px-4 py-3 min-w-[160px]">{r.target}</td>
    <td className="px-4 py-3 text-xs text-gray-600 min-w-[200px]">{r.details}</td>
  </tr>
);

export default function ActivityLog() {
  const canExport = useCanExport();
  const [f, setF] = useState(EMPTY);
  const [rows, setRows] = useState(null);
  const pg = usePaged(rows || [], rows);
  const [staff, setStaff] = useState([]);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== ALL));
    return api.get("/activity", { params }).then((r) => setRows(r.data));
  }, [f]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)); }, []);
  return (
    <div data-testid="activity-log-page">
      <PageHeader eyebrow="Super Admin" title="Staff Activity Log" description="Who did what and when across EventHub. Entries cannot be edited and are kept for 12 months."
        actions={canExport && <BrandButton variant="outline" onClick={() => exportCsv(rows || [])} disabled={!rows?.length} data-testid="activity-export-csv"><Download className="w-4 h-4" />Export CSV</BrandButton>} />
      <div className="bg-white border border-[#E5E7EB] rounded-lg">
        <Filters f={f} set={set} staff={staff} />
        <div className="px-4 py-2 text-xs text-gray-500 border-b" data-testid="activity-count">{rows ? `${rows.length} entr${rows.length === 1 ? "y" : "ies"}${rows.length >= 1000 ? " (latest 1,000 shown, narrow the filters to see more)" : ""}` : "Loading…"}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="activity-table">
            <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
              <tr>{["When", "Staff member", "Category", "Action", "Affected", "Details"].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y">{pg.items.map((r) => <Row key={r.id} r={r} />)}</tbody>
          </table>
          <Pager {...pg} testId="activity-pager" />
          {rows?.length === 0 && <div className="p-10 text-center text-sm text-gray-500" data-testid="activity-empty">No activity matches these filters.</div>}
        </div>
      </div>
    </div>
  );
}
